// Alder, friskhet og sensorstatus — grunnregelen i hele sensorflata.
//
// EIERKRAVET, ORDRETT: «En sensor som har vært stille i 40 s skal SE stille ut.»
//
// Det er derfor dette er en egen, ren fil med egne tester og ikke et par
// if-setninger inne i sensorstolpen. En operatør alene i mørket leser den
// venstre stolpen som et løfte: står det noe der, er det sett NÅ. Om en sensor
// som falt for to minutter siden fortsatt viser sin siste peiling med samme
// vekt som en fersk, er hele flata en løgn — og det er nettopp den feilen som
// er lettest å gjøre, fordi tallet fra i går ser like fint ut som tallet fra nå.
//
// HVORFOR DETTE ER DEN MEST VERDIFULLE FILA Å DELE: spørsmålet «hvor gammel er
// målingen» har akkurat samme svar for en hydrofon på 30 meters dybde og for en
// vibrasjonsdetektor i en grøft. Ingenting her vet hva sensoren måler — bare når
// den sist sa noe, og hva broen mener om den.
//
// Hvorfor egne terskler og ikke `sensorHealth` fra contracts: den vurderer
// SPARK-nodens HELSE (mesh-link, batteri, ferskhet på nodemeldinger). Her er
// spørsmålet et annet — hvor gammel er MÅLINGEN — og en radar kan være helt
// frisk som node samtidig som den ikke har sett noe på fem minutter. De to
// svarene må kunne være ulike, ellers mister operatøren skillet mellom «sensoren
// er nede» og «sensoren ser ingenting».
// ── Alder som tekst ─────────────────────────────────────────────────────────
/**
 * Alder i klartekst. Reglene er valgt for lesbarhet PÅ AVSTAND, med hansker og
 * i mørket — ikke for presisjon:
 *   * under 1 s  → «nå»       (sifre som stryker over 0 er bare uro)
 *   * under 1 min → hele sekunder
 *   * under 1 t   → hele minutter
 *   * under 24 t  → timer + minutter (minuttene betyr noe i en vakt)
 *   * over        → «> 24 t»  (da er tallet uinteressant, tilstanden er «gammelt»)
 * Negativ alder (klokka til broen går foran vår) rapporteres som «nå» framfor
 * «-3 s»: klokkeavvik er en driftssak, ikke noe operatøren skal tolke midt i en
 * observasjon.
 */
export function formaterAlder(alderMs) {
    if (!Number.isFinite(alderMs) || alderMs < 1000)
        return 'nå';
    const s = Math.floor(alderMs / 1000);
    if (s < 60)
        return `${s} s`;
    const min = Math.floor(s / 60);
    if (min < 60)
        return `${min} min`;
    const t = Math.floor(min / 60);
    if (t < 24) {
        const restMin = min - t * 60;
        return restMin === 0 ? `${t} t` : `${t} t ${restMin} min`;
    }
    return '> 24 t';
}
/** Klokkeslett HH:MM:SS i lokal tid — det operatøren fører i vaktjournalen. */
export function formaterKlokke(tsMs) {
    if (!Number.isFinite(tsMs))
        return '--:--:--';
    const d = new Date(tsMs);
    const t = (n) => String(n).padStart(2, '0');
    return `${t(d.getHours())}:${t(d.getMinutes())}:${t(d.getSeconds())}`;
}
export const FRISKHET_NAVN = {
    fersk: 'Fersk',
    sen: 'Sen',
    stille: 'Stille',
    borte: 'Borte',
};
/**
 * Standardtersklene. `stilleMs: 40_000` er eierkravet direkte: 40 sekunder uten
 * måling SKAL se stille ut. `senMs` ligger på 10 s fordi det er omtrent fire
 * radaromdreininger — nok til at etterslepet er reelt og ikke bare jitter.
 * `borteMs` på 5 min er der en måling slutter å være situasjonsbilde og blir
 * logg.
 */
export const STANDARD_TERSKLER = { senMs: 10_000, stilleMs: 40_000, borteMs: 300_000 };
/**
 * Terskler for én sensor. Melder broen et forventet meldeintervall, skaleres
 * tersklene av det — en hydrofon som strømmer kontinuerlig er «sen» lenge før
 * en RF-sveip som normalt bruker 30 s per runde. 40-sekundersregelen holdes som
 * et TAK: uansett hvor treg sensoren sier den er, skal 40 s stillhet se stille
 * ut, for det er operatørens tålegrense og ikke sensorens.
 */
export function terskelFor(sensor, base = STANDARD_TERSKLER) {
    const iv = sensor.forventetIntervallMs;
    if (iv === undefined || !Number.isFinite(iv) || iv <= 0)
        return base;
    return {
        senMs: Math.max(1000, Math.round(iv * 2)),
        stilleMs: Math.min(base.stilleMs, Math.max(2000, Math.round(iv * 4))),
        borteMs: Math.max(base.stilleMs, Math.round(iv * 30)),
    };
}
/** Alder → friskhet. Grensene er inklusive: 40 000 ms ER stille. */
export function friskhet(alderMs, t = STANDARD_TERSKLER) {
    if (!Number.isFinite(alderMs))
        return 'borte';
    if (alderMs >= t.borteMs)
        return 'borte';
    if (alderMs >= t.stilleMs)
        return 'stille';
    if (alderMs >= t.senMs)
        return 'sen';
    return 'fersk';
}
export const NIVAA_NAVN = {
    ok: 'OK',
    degradert: 'DEGRADERT',
    stille: 'STILLE',
    nede: 'NEDE',
    av: 'AV',
};
const NIVAA_RANG = { ok: 0, av: 1, degradert: 2, stille: 3, nede: 4 };
/**
 * Nivå → CSS-klassesuffiks. Ligger her og ikke i appene fordi den var ordrett
 * duplisert i NJORDs og ULLs `main.ts`, og fordi den avgjør hvilken FARGE en
 * sensorrad får: to flater som gir ulik farge til samme nivå er ikke ett
 * designsystem.
 *
 * At navnene ikke er identiske med nivåene er med vilje — klassenavnene er
 * FRISKHETENS ordforråd (`frisk`/`treg`/`stille`/`borte`), som stilarkene bruker
 * på både sensorrader, aldre og kontaktkort. Oversettelsen står derfor ett sted.
 */
export function nivaaKlasse(n) {
    switch (n) {
        case 'ok':
            return 'frisk';
        case 'degradert':
            return 'treg';
        case 'stille':
            return 'stille';
        case 'nede':
            return 'borte';
        default:
            return 'av';
    }
}
/** Verste nivå av to. Brukes til postens samlede sensortilstand i toppstripa. */
export function versteNivaa(a, b) {
    return NIVAA_RANG[a] >= NIVAA_RANG[b] ? a : b;
}
/**
 * Vurder én sensor. `sisteMaalingMs` er tidspunktet for siste OBSERVASJON fra
 * sensoren — ikke siste hjertebank. Det er et bevisst valg: en sensor som
 * hjertebanker men ikke måler, er stille, og skal se stille ut.
 *
 * Rekkefølgen på reglene er rangert etter hva som gjør sensoren ubrukelig
 * FØRST. En avslått sensor er avslått uansett hvor fersk siste måling er, og en
 * feilmeldt sensor skal ikke kunne se «OK» fordi den tilfeldigvis rakk å sende
 * et ekko før den falt.
 */
export function sensorStatus(sensor, sisteMaalingMs, naaMs, base = STANDARD_TERSKLER) {
    const t = terskelFor(sensor, base);
    const alderMs = sisteMaalingMs === null ? null : Math.max(0, naaMs - sisteMaalingMs);
    const f = alderMs === null ? 'borte' : friskhet(alderMs, t);
    const grunner = [];
    if (sensor.tilstand === 'av') {
        return { sensorId: sensor.id, nivaa: 'av', friskhet: f, alderMs, grunner: [sensor.merknad ?? 'Slått av'] };
    }
    if (sensor.tilstand === 'feil') {
        grunner.push(sensor.merknad ?? 'Broen melder feil');
        return { sensorId: sensor.id, nivaa: 'nede', friskhet: f, alderMs, grunner };
    }
    if (sensor.tilstand === 'ukjent') {
        grunner.push('Broen melder ingen tilstand');
    }
    if (sensor.tilstand === 'degradert') {
        grunner.push(sensor.merknad ?? 'Broen melder degradert');
    }
    if (alderMs === null) {
        grunner.push('Har ikke meldt en eneste måling');
        return { sensorId: sensor.id, nivaa: 'nede', friskhet: 'borte', alderMs, grunner };
    }
    if (f === 'borte' || f === 'stille') {
        grunner.push(`Ingen måling på ${formaterAlder(alderMs)}`);
        return { sensorId: sensor.id, nivaa: f === 'borte' ? 'nede' : 'stille', friskhet: f, alderMs, grunner };
    }
    if (f === 'sen') {
        grunner.push(`Siste måling ${formaterAlder(alderMs)} gammel`);
        return { sensorId: sensor.id, nivaa: 'degradert', friskhet: f, alderMs, grunner };
    }
    const nivaa = sensor.tilstand === 'degradert' || sensor.tilstand === 'ukjent' ? 'degradert' : 'ok';
    return { sensorId: sensor.id, nivaa, friskhet: f, alderMs, grunner };
}
export function postTillit(statuser) {
    let verste = 'ok';
    let friske = 0;
    for (const s of statuser) {
        if (s.nivaa === 'ok')
            friske++;
        verste = versteNivaa(verste, s.nivaa);
    }
    return { friske, totalt: statuser.length, verste };
}
/** Sensorer som trenger oppmerksomhet, verst først. Tom liste = alt måler. */
export function trengerTilsyn(statuser) {
    return statuser
        .filter((s) => s.nivaa !== 'ok')
        .sort((a, b) => NIVAA_RANG[b.nivaa] - NIVAA_RANG[a.nivaa]);
}
/** Er den meldte tilstanden noe operatøren må gjøre noe med? */
export function erMeldtProblem(t) {
    return t === 'degradert' || t === 'feil';
}
//# sourceMappingURL=friskhet.js.map